// KOT (Kitchen Order Ticket) avtomatik çapı.
//
// Front-un POS masa səhifəsində "Mutfak" düyməsi cart-dakı hər məhsul üçün
// `POST /tables/:id/add-stock` çağırır (bax: components/pos/PosTablePage.tsx
// `sendToKitchen`). Bu, semantik olaraq "bunları mətbəxə göndər" deməkdir.
//
// pos_local-da KOT çapı `POST /v1/orders/:id/send-to-kitchen` ilə işə düşür
// (kitchen/bar printer qeydiyyatlıdırsa avtomatik çap + `orders.OrderSentToKitchen`
// WS event-i). Gateway-in add-stock route-u yalnız `/items` çağırır, ona görə
// KOT heç vaxt çap olunmurdu.
//
// Həll: hər add-stock-dan sonra send-to-kitchen-i DEBOUNCE ilə planlaşdırırıq.
// Bir "Mutfak" klikləməsi qısa fasilədə N add-stock yaradır → onları birləşdirib
// TƏK send-to-kitchen çağırırıq → bütün yeni sətirlər üçün tək KOT. Komponent
// dəyişmir; çap UI axınını bloklamır (response artıq qaytarılıb).

import { posRequest } from './posClient';

const KOT_DEBOUNCE_MS = 400;

const timers = new Map<string, ReturnType<typeof setTimeout>>();
/** Debounce pəncərəsində ofisantın seçdiyi mətbəx (order-ə görə). */
const pendingKitchen = new Map<string, string | null>();

/**
 * Order-i mətbəxə göndər (KOT çapı). Xəta sifariş axınını pozmur.
 *
 * `kitchenId` verilsə (`kit_7`) KOT məhz həmin mətbəxin printerinə gedir;
 * verilməsə pos_local avtomatik marşrutlaşdırır: məhsul → kateqoriya →
 * mətbəx → printer.
 */
export async function sendOrderToKitchen(
  orderId: string,
  kitchenId?: string | null,
): Promise<void> {
  try {
    await posRequest({
      method: 'POST',
      path: `/v1/orders/${orderId}/send-to-kitchen`,
      body: kitchenId ? { kitchenId } : {},
    });
  } catch {
    // printer offline / şəbəkə xətası — sifariş onsuz da yaranıb, çapı udmaq olar.
    // NOTHING_TO_SEND (409) da bura düşür: göndəriləcək yeni sətir yoxdur.
  }
}

/**
 * add-stock burst-undan sonra tək KOT çıxsın deyə send-to-kitchen-i debounce edir.
 * Eyni order üçün ardıcıl çağırışlar birləşir.
 *
 * Burst boyunca sonuncu verilən `kitchenId` qalır — ofisant bir "göndər"
 * əməliyyatında tək mətbəx seçir, ona görə bu düzgün semantikadır.
 */
export function scheduleSendToKitchen(orderId: string, kitchenId?: string | null): void {
  const existing = timers.get(orderId);
  if (existing) clearTimeout(existing);
  if (kitchenId !== undefined) pendingKitchen.set(orderId, kitchenId);
  const t = setTimeout(() => {
    timers.delete(orderId);
    const chosen = pendingKitchen.get(orderId) ?? null;
    pendingKitchen.delete(orderId);
    void sendOrderToKitchen(orderId, chosen);
  }, KOT_DEBOUNCE_MS);
  timers.set(orderId, t);
}

/** Test üçün — gözləyən bütün taymerləri ləğv et. */
export function __resetKitchenTimers(): void {
  for (const t of timers.values()) clearTimeout(t);
  timers.clear();
  pendingKitchen.clear();
}
